import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthPageShell from '../components/AuthPageShell';
import FullScreenSpinner from '../components/FullScreenSpinner';
import { useAuth } from '../contexts/AuthContext';
import { getNextAuthRoute } from '../utils/authFlow';

export default function MfaChallenge() {
  const {
    cancelMfaChallenge,
    completeMfaChallenge,
    isAuthenticated,
    isEmailVerified,
    isLoading,
    isMfaChallengeRequired,
    isMfaEnrolled,
    mfaHintLabel,
  } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (!isMfaChallengeRequired) {
    return (
      <Navigate
        to={getNextAuthRoute({
          isAuthenticated,
          isEmailVerified,
          isMfaChallengeRequired,
          isMfaEnrolled,
        })}
        replace
      />
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await completeMfaChallenge(verificationCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify your authenticator code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    cancelMfaChallenge();
  };

  return (
    <AuthPageShell
      title="Enter Your Authenticator Code"
      description="This account already has TOTP multi-factor authentication enabled."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 p-4 text-sm text-blue-900 dark:text-blue-200">
          Use the current code from {mfaHintLabel ?? 'your authenticator app'} to continue.
        </div>
        {error ? (
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : null}
        <div>
          <label
            htmlFor="mfa-code"
            className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
          >
            Authenticator code
          </label>
          <input
            type="text"
            id="mfa-code"
            value={verificationCode}
            onChange={(event) => setVerificationCode(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white transition-colors duration-200"
            placeholder="Enter the 6-digit code"
            required
          />
        </div>
        <button
          type="submit"
          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Verifying...' : 'Verify and continue'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="w-full text-sm text-gray-600 dark:text-gray-300 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          Back to login
        </button>
      </form>
    </AuthPageShell>
  );
}
